import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Loader2, Search, RefreshCw, Link2, CreditCard, Landmark, ExternalLink, LinkIcon, ShieldCheck } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Label } from '../ui/label';
import { Link as RouterLink } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'sonner';
import AutoPayReadinessPanel from './AutoPayReadinessPanel';

/**
 * Admin Payment Settings → "Saved PMs" tab (iter244).
 *
 * Shows every parent + customer with their CC / Bank PM counts, lets
 * admins filter by role / search, and one-click auto-link every
 * unlinked user against Stripe by email.
 */
export default function PaymentMethodsDirectory() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total: 0, linked: 0, unlinked: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [pmFilter, setPmFilter] = useState('any');
  const [bulkLinking, setBulkLinking] = useState(false);
  const [refreshingRowId, setRefreshingRowId] = useState(null);
  // iter244: page size + page index for client-side pagination. Server
  // returns up to 500 rows; we slice for display.
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (search) params.set('search', search);
      if (roleFilter !== 'all') params.set('role', roleFilter);
      if (pmFilter !== 'any') params.set('has_pm', pmFilter);
      const { data } = await api.get(`/stripe/admin/pm-directory?${params.toString()}`);
      setRows(data.rows || []);
      setTotals({ total: data.total || 0, linked: data.linked_count || 0, unlinked: data.unlinked_count || 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load PM directory');
    } finally { setLoading(false); }
  };

  // Initial load
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, []);

  // Debounced filter re-fetch
  useEffect(() => {
    const t = setTimeout(fetchRows, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, roleFilter, pmFilter]);

  const handleBulkLink = async () => {
    if (!window.confirm(
      'Search Stripe by email for every unlinked parent + customer and auto-link any unique matches?\n\n' +
      'This processes up to 100 unlinked users per click in parallel (~15 seconds). Safe to run repeatedly — already-linked users are skipped, and any remaining will be reported so you can re-run.'
    )) return;
    setBulkLinking(true);
    try {
      const { data } = await api.post('/stripe/admin/auto-link-all-customers?max_to_process=100', null, { timeout: 60000 });
      const remaining = data.remaining || 0;
      const errCount = data.errors?.length || 0;
      const backfilled = data.backfilled_from_parent_profiles || 0;
      const tail = (remaining > 0 ? ` · ${remaining} still unlinked — click again to continue` : '')
                 + (errCount > 0 ? ` · ${errCount} errors` : '')
                 + (backfilled > 0 ? ` · ${backfilled} backfilled from existing profile data` : '');
      toast.success(
        `Processed ${data.processed} — linked ${data.matched} · no Stripe match for ${data.no_stripe_record}` + tail,
        { duration: 9000 }
      );
      // iter247: surface a sample of distinct error messages so the
      // admin can see WHY things failed (rate-limit, invalid email, etc.)
      // instead of just a "21 errors" count with no context.
      if (data.error_samples && data.error_samples.length > 0) {
        toast.error(
          `Sample errors:\n${data.error_samples.join('\n')}`,
          { duration: 15000, style: { whiteSpace: 'pre-wrap', maxWidth: '600px' } }
        );
      }
      await fetchRows();
    } catch (e) {
      const detail = e?.response?.data?.detail
        || e?.message
        || (e?.code === 'ECONNABORTED' ? 'Auto-link timed out — try again (it now processes in batches)' : null)
        || 'Auto-link failed';
      toast.error(detail, { duration: 7000 });
    } finally { setBulkLinking(false); }
  };

  // Reset to first page when filters change.
  useEffect(() => { setPageIndex(0); }, [search, roleFilter, pmFilter]);

  const refreshOne = async (userId) => {
    // iter244: update ONLY this row in state instead of re-fetching the
    // entire directory — previous behavior re-rendered the whole table
    // (~108 rows) every time the admin clicked a row's refresh icon.
    setRefreshingRowId(userId);
    try {
      const { data } = await api.get(`/stripe/admin/payment-methods/${userId}`);
      const pms = data.payment_methods || [];
      const cards_count = pms.filter((p) => p.type === 'card').length;
      const banks_count = pms.filter((p) => p.type === 'bank_account' || p.type === 'us_bank_account').length;
      setRows((prev) => prev.map((r) => r.user_id === userId ? {
        ...r,
        cards_count,
        banks_count,
        stripe_customer_id: data.stripe_customer_id || r.stripe_customer_id,
        linked: !!(data.stripe_customer_id || r.stripe_customer_id),
        last_synced_at: new Date().toISOString(),
      } : r));
    } catch {
      toast.error('Could not refresh this user');
    } finally {
      setRefreshingRowId(null);
    }
  };

  // iter255 — Manual link modal state + handler
  const [manualLinkOpen, setManualLinkOpen] = useState(false);
  const [manualLinkRow, setManualLinkRow] = useState(null);
  const [manualLinkMode, setManualLinkMode] = useState('by_email');
  const [manualLinkValue, setManualLinkValue] = useState('');
  const [manualLinking, setManualLinking] = useState(false);

  const openManualLink = (row) => {
    setManualLinkRow(row);
    setManualLinkMode('by_email');
    setManualLinkValue('');
    setManualLinkOpen(true);
  };

  const submitManualLink = async () => {
    const value = manualLinkValue.trim();
    if (!value) {
      toast.error(manualLinkMode === 'by_id' ? 'Enter a Stripe customer ID (cus_...)' : 'Enter an alternate email');
      return;
    }
    if (manualLinkMode === 'by_id' && !value.startsWith('cus_')) {
      toast.error('Stripe customer ID must start with "cus_"');
      return;
    }
    if (manualLinkMode === 'by_email' && !value.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setManualLinking(true);
    try {
      const { data } = await api.post('/stripe/admin/manual-link-customer', {
        user_id: manualLinkRow.user_id,
        mode: manualLinkMode,
        value,
      });
      toast.success(
        `Linked ${data.user_name} → ${data.stripe_customer_id.slice(0, 16)}…` +
        (data.alt_email_used ? ` via ${data.alt_email_used}` : ''),
        { duration: 7000 }
      );
      setManualLinkOpen(false);
      // Update only this row in state; bulk fetch not needed.
      setRows((prev) => prev.map((r) => r.user_id === manualLinkRow.user_id ? {
        ...r,
        stripe_customer_id: data.stripe_customer_id,
        linked: true,
        manual_link: true,
        stripe_alt_email: data.alt_email_used || r.stripe_alt_email,
      } : r));
      // Then re-fetch PMs for that one row so card/bank counts populate.
      refreshOne(manualLinkRow.user_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Manual link failed');
    } finally {
      setManualLinking(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="pm-directory-tab">
      <AutoPayReadinessPanel />
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Saved Payment Methods</CardTitle>
              <CardDescription>
                CC and bank account on file per customer/parent. {totals.linked} of {totals.total} are linked to a Stripe customer record.
              </CardDescription>
            </div>
            <Button onClick={handleBulkLink} disabled={bulkLinking} data-testid="bulk-auto-link-btn">
              {bulkLinking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
              Search Stripe by email & link if found
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-5 relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input className="pl-8" placeholder="Search by name, email, or phone…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pm-directory-search" />
            </div>
            <div className="col-span-3">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="pm-directory-role-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="parent">Parents Only</SelectItem>
                  <SelectItem value="customer">Customers Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <Select value={pmFilter} onValueChange={setPmFilter}>
                <SelectTrigger data-testid="pm-directory-pm-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any PM Status</SelectItem>
                  <SelectItem value="either">Has CC or Bank</SelectItem>
                  <SelectItem value="card">Has Card</SelectItem>
                  <SelectItem value="bank">Has Bank</SelectItem>
                  <SelectItem value="none">Linked but no PM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1">
              <Button variant="outline" size="icon" onClick={fetchRows} title="Refresh"><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-center"><CreditCard className="h-4 w-4 inline mr-1" />CC</TableHead>
                <TableHead className="text-center"><Landmark className="h-4 w-4 inline mr-1" />Bank</TableHead>
                <TableHead>Stripe Customer</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400 text-sm">No users match.</TableCell></TableRow>
              ) : rows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize).map((r) => {
                const detailPath = r.role === 'parent' ? `/admin/parents/${r.user_id}` : `/admin/customers/${r.user_id}`;
                return (
                  <TableRow key={r.user_id} data-testid={`pm-row-${r.user_id}`}>
                    <TableCell className="font-medium">
                      <RouterLink to={detailPath} className="text-blue-600 hover:underline">{r.name}</RouterLink>
                    </TableCell>
                    <TableCell className="text-xs">{r.email}</TableCell>
                    <TableCell>
                      <Badge variant={r.role === 'parent' ? 'default' : 'secondary'} className="capitalize">{r.role}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {r.cards_count == null ? <span className="text-slate-300 text-xs">—</span>
                        : r.cards_count > 0 ? <Badge className="bg-emerald-100 text-emerald-700">{r.cards_count}</Badge>
                        : <span className="text-slate-400 text-xs">0</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {r.banks_count == null ? <span className="text-slate-300 text-xs">—</span>
                        : r.banks_count > 0 ? <Badge className="bg-blue-100 text-blue-700">{r.banks_count}</Badge>
                        : <span className="text-slate-400 text-xs">0</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.linked ? (
                        <div className="flex items-center gap-1">
                          <a href={`https://dashboard.stripe.com/customers/${r.stripe_customer_id}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                            {r.stripe_customer_id.slice(0, 16)}…<ExternalLink className="h-3 w-3" />
                          </a>
                          {r.manual_link && (
                            <span title={r.stripe_alt_email ? `Manually linked via alt email ${r.stripe_alt_email}` : 'Manually linked by an admin'}>
                              <ShieldCheck className="h-3 w-3 text-indigo-500" />
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-amber-700">Not linked</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => refreshOne(r.user_id)} title="Re-fetch PMs from Stripe" data-testid={`refresh-pm-${r.user_id}`} disabled={refreshingRowId === r.user_id}>
                          {refreshingRowId === r.user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openManualLink(r)} title={r.linked ? 'Re-link to a different Stripe customer' : 'Manually link this user to a Stripe customer'} data-testid={`manual-link-${r.user_id}`}>
                          <LinkIcon className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between text-xs text-slate-500 pt-2">
            <span>Showing <strong>{rows.length === 0 ? 0 : pageIndex * pageSize + 1}–{Math.min((pageIndex + 1) * pageSize, rows.length)}</strong> of <strong>{rows.length}</strong> filtered users</span>
            <div className="flex items-center gap-2">
              <span>Per page:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPageIndex(0); }}>
                <SelectTrigger className="h-7 w-20 text-xs" data-testid="pm-directory-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[25, 50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" disabled={pageIndex === 0} onClick={() => setPageIndex((p) => Math.max(0, p - 1))} data-testid="pm-directory-prev">Prev</Button>
              <span data-testid="pm-directory-page-indicator">Page {pageIndex + 1} of {Math.max(1, Math.ceil(rows.length / pageSize))}</span>
              <Button variant="outline" size="sm" disabled={(pageIndex + 1) * pageSize >= rows.length} onClick={() => setPageIndex((p) => p + 1)} data-testid="pm-directory-next">Next</Button>
            </div>
          </div>

          <p className="text-xs text-slate-400">
            CC / Bank counts come from a local cache that's updated when you open a Parent/Customer detail page. "—" means cache miss — click the refresh icon to fetch live from Stripe.
          </p>
        </CardContent>
      </Card>

      {/* iter255 — Manual Stripe customer link modal */}
      <Dialog open={manualLinkOpen} onOpenChange={setManualLinkOpen}>
        <DialogContent className="max-w-md" data-testid="manual-link-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LinkIcon className="h-4 w-4 text-indigo-600" />Manually link Stripe customer</DialogTitle>
            <DialogDescription>
              {manualLinkRow ? (
                <span>
                  Linking <strong>{manualLinkRow.name}</strong> ({manualLinkRow.email || 'no portal email'}).{' '}
                  Use this when the portal email doesn't match the Stripe customer's email
                  (e.g. parent checked out with a different address).
                </span>
              ) : 'Pick how to identify the Stripe customer.'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={manualLinkMode} onValueChange={setManualLinkMode} className="mt-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="by_email" data-testid="manual-link-tab-email">By alternate email</TabsTrigger>
              <TabsTrigger value="by_id" data-testid="manual-link-tab-id">By Stripe customer ID</TabsTrigger>
            </TabsList>
            <TabsContent value="by_email" className="mt-3 space-y-2">
              <Label>Alternate email to search Stripe for</Label>
              <Input
                placeholder="e.g. parent_alt@gmail.com"
                value={manualLinkValue}
                onChange={(e) => setManualLinkValue(e.target.value)}
                autoFocus
                data-testid="manual-link-email-input"
              />
              <p className="text-xs text-slate-500">We'll run <code>stripe.Customer.list(email=…)</code> and pick the best match (default payment method first, then newest customer).</p>
            </TabsContent>
            <TabsContent value="by_id" className="mt-3 space-y-2">
              <Label>Stripe Customer ID</Label>
              <Input
                placeholder="cus_…"
                value={manualLinkValue}
                onChange={(e) => setManualLinkValue(e.target.value)}
                autoFocus
                data-testid="manual-link-id-input"
              />
              <p className="text-xs text-slate-500">Find this on the customer's page in the Stripe dashboard. We'll verify the customer exists before linking.</p>
            </TabsContent>
          </Tabs>

          <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 rounded p-2 mt-3">
            Once linked manually, this user is <strong>permanently excluded</strong> from the bulk auto-link pass so a future search by portal email can't overwrite this decision.
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManualLinkOpen(false)} data-testid="manual-link-cancel">Cancel</Button>
            <Button onClick={submitManualLink} disabled={manualLinking || !manualLinkValue.trim()} data-testid="manual-link-submit">
              {manualLinking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LinkIcon className="h-4 w-4 mr-2" />}
              Link customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
